/**
 * The trips and account pages' warning that a membership payment failed — a renewal, or the first
 * charge after a trial — while Stripe is still retrying it.
 *
 * The member still has the app at this point (`past_due` entitles), so this is the one window to
 * tell them before retries run out and the lock takes over. The remedy is Stripe's portal, where they
 * update the card and the outstanding invoice is retried; the card form would mint a second
 * subscription beside the one that needs paying.
 *
 * Opens through {@link useBillingPortal}, so it shares the account menu's same-tab redirect and its
 * failure toast.
 */
import { useMembershipPaymentState, useUserProfileState } from '#states/hooks.js';
import { Alert, AlertDescription, AlertTitle, Button } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { useBillingPortal } from '#views/profiles/use-billing-portal.js';

export function PaymentFailedAlert() {
  const membershipPaymentState = useMembershipPaymentState();
  const userProfileState = useUserProfileState();
  const billingPortal = useBillingPortal(userProfileState.profile?.subscription);

  if (!membershipPaymentState.hasFailedPayment) return;

  return (
    <Alert variant="destructive" className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <AlertTitle>We couldn’t renew your membership</AlertTitle>
        <AlertDescription>Update your payment details to keep your Elara membership.</AlertDescription>
      </div>
      <Button
        variant="destructive"
        className="shrink-0"
        disabled={billingPortal.isOpening}
        onClick={() => void billingPortal.openPortal()}
      >
        Update payment details
        {/* Leaves Elara for Stripe's hosted portal, so it says so, as the account menu's row does. */}
        <MaterialIcon icon="arrow_outward" size={IconSizes.sm} aria-hidden="true" />
      </Button>
    </Alert>
  );
}
